import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { of } from 'rxjs';
import { take, catchError, mergeMap } from 'rxjs/operators';
import { ProductGenericGridResponseModel} from 'abs-ngx-library/src/lib/core';
import { CommonService, UserInfoService , UtilService } from 'abs-ngx-library/src/lib/core';
import { EnviormentConfig } from 'abs-ngx-library/src/lib/core';

declare let $: any;

@Injectable()
export class ProductCatalogALV1Service {
    public httpOptions: any;

    // DFTA - wcax env config
    private config = EnviormentConfig;

    public currentEnv = '';

    public constructor(
            private _http: HttpClient, 
            private _commonService: CommonService,
            private _userInfoService: UserInfoService,
            private _utilService : UtilService
          )
    {   
        this.currentEnv = this._commonService.getCurrentEnviorment();
        this.httpOptions = {
            headers: new HttpHeaders({
                'ocp-apim-subscription-key': this.getSubscriptionKey(this.config[this.currentEnv])
            })
        };
    }

    /**
    * This method returns either mockinator or search api subscription key.
    */
    private getSubscriptionKey(envConfig: any) {
        const wiremockAPIDetails = envConfig?.wiremockAPIDetails;
        const mode = this._utilService.getParams('mode');
        // check if wiremock service is enabled
        if (wiremockAPIDetails?.allowWiremockService && (mode === 'record' || mode === 'playback')) {
            return wiremockAPIDetails?.subscriptionKey;
        }
        return envConfig?.searchAPIDetails?.subscriptionKey?.value;
    }

    /**
     * Method used for launching an HTTP request for BPN products.
     *
     * @param bpnString - BPN ids for the products
     */
    public getCatalogXapiProductPrice(bpnString: string) {

        const productsByBpnEndpoint = `${window['AB']['catalogXapiProductsBpnEndpoint']}`
            + `?store-id=${this._userInfoService.getShopStoreId()}`
            + `&includeOffer=true`
            + `&bpn=${bpnString}`;

        return this._http.get(productsByBpnEndpoint, this.httpOptions);
    }

    /**
     * Method used for launching an HTTP request for UPC products.
     *
     * @param upcString - UPC ids for the products
     * @param sellerIdString - marketplace product's seller id
     */
    public getCatalogXapiProductPriceByUPC(upcString: string, isGuestUser?: boolean, hasHHId?: boolean, appendChannel?: boolean, sellerId?: string, sellerIdString?: string, badgePreference?: string, badgeList?: string) {
        let productsByUpcEndpoint = `${window['AB']['catalogXapiProductsUpcEndpoint']}`
            + `?upc=${upcString}&banner=${window['AB']['userInfo']['banner']}&includeOffer=true`;

        if (isGuestUser) {
            productsByUpcEndpoint = `${productsByUpcEndpoint}&guest=${isGuestUser}`;
        }

        if (hasHHId) {
            const hhid = window['AB'] && window['AB']['userInfo'] && window['AB']['userInfo']['houseId']
                ? window['AB']['userInfo']['houseId']
                : '';
            productsByUpcEndpoint = `${productsByUpcEndpoint}&hhid=${hhid}`;
        }

        if (appendChannel) {
            productsByUpcEndpoint = this._commonService.generateCBAUrl(productsByUpcEndpoint);
        }
        // check if product is a marketplace product
        if (sellerIdString) {
            productsByUpcEndpoint = `${productsByUpcEndpoint}&seller-id=${sellerIdString}`;
        }

        // check if product is a marketplace product
        if (sellerId) {
            productsByUpcEndpoint = `${productsByUpcEndpoint}&sellerId=${sellerId}`;
        }

        // Add badge parameters if provided
        if (badgePreference && badgeList) {
            productsByUpcEndpoint = `${productsByUpcEndpoint}&${badgePreference}=${badgeList}`;
        }

        // README: Make the api call only if storeId exists
        if (window['AB']['userInfo']['branchId']) {
            productsByUpcEndpoint = `${productsByUpcEndpoint}&store-id=${window['AB']['userInfo']['branchId']}`;

            return this._http.get(productsByUpcEndpoint, this.httpOptions);
        }

        return of({});
    }

    public getCatalogData(objGridData: any) {

        let url = '';
        let strUrl = '';
        const wiremockAPIDetails = this.config[this.currentEnv].wiremockAPIDetails;
        const mode = wiremockAPIDetails?.allowWiremockService ? this._utilService.getParams('mode') : '';
        switch (objGridData.apiType) {

            case 'c-bogo':
            case 'c-club-card':
                break;

            case 'c-virtual-shelf-general':
            case 'c-virtual-shelf-promo':

                const virtualShelfId = objGridData.value[objGridData.apiType]['virtualShelfId'];
                const banner = window['AB']['userInfo']['banner'];

                strUrl = objGridData.apiUrl
                    + '?storeId=' + window['AB']['userInfo']['branchId']
                    + '&virtualShelfId=' + virtualShelfId + `&banner=${banner}`
                    + '&page=0&pageSize=999';
                    + `&includeOffers=true`;

                // Add badge parameters if provided
                if (objGridData?.badgePreference && objGridData?.badgeList) {
                    strUrl = `${strUrl}&${objGridData.badgePreference}=${objGridData.badgeList}`;
                }

                url = this._commonService.generateCBAUrl(strUrl);
                // check if wiremock service is enabled
                if (wiremockAPIDetails?.allowWiremockService) {
                    // check mode coming from request parameter
                    if (mode === 'record') {
                        // prepend record base url to virtual shelf api endpoint
                        url = `${wiremockAPIDetails?.recorderBaseUrl}${url}`;
                    } else if (mode === 'playback') {
                        // prepend playback base url to virtual shelf api endpoint
                        url = `${wiremockAPIDetails?.playbackBaseUrl}${url}`;
                    }
                }
                return this._http.get<ProductGenericGridResponseModel>(url, this.httpOptions);

            case 'c-products-by-upc':
            case 'c-products-by-upc-drawer':
                const storeId = this._userInfoService.getShopStoreId();
                return this._commonService.$upcs
                    .pipe(
                        take(1),
                        mergeMap((arrUpc: any) => {
                            if (arrUpc && arrUpc.length > 0) {
                                const channel = this._userInfoService.getUserChannel();
                                const banner = this._userInfoService.getBanner();
                                const body = { 'ids': arrUpc, storeId, banner };
                                let requestBody: any =  channel ? { ...body, channel } : body;
                                requestBody = { ...requestBody, includeOffer: true };
    
                                // Add badge parameters if provided
                                if (objGridData.badgePreference && objGridData.badgeList) {
                                    requestBody[objGridData.badgePreference] = objGridData.badgeList;
                                }

                                url = objGridData.apiUrl;
                                // check if wiremock service is enabled
                                if (wiremockAPIDetails?.allowWiremockService) {
                                    // check mode coming from request parameter
                                    if (mode === 'record') {
                                        // prepend record base url to products-by-upc api endpoint
                                        url = `${wiremockAPIDetails?.recorderBaseUrl}${url}`;
                                    } else if (mode === 'playback') {
                                        // prepend playback base url to products-by-upc api endpoint
                                        url = `${wiremockAPIDetails?.playbackBaseUrl}${url}`;
                                    }
                                }

                                return this._http
                                    .post<ProductGenericGridResponseModel>(url, requestBody , this.httpOptions)
                                    .pipe(
                                        catchError(() => of({ ack: true, response: { docs: [], offersData: {} } }))
                                    );
    
                            } else {
                                return of({ ack: true, response: { docs: [], offersData: {} } });
                            }
                        })
                    );

            default:
                break;
        }
    }
}
